import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

function prepararDocumento(janela, titulo) {
  const origem = window.document;
  const destino = janela.document;
  destino.title = titulo;
  destino.documentElement.lang = origem.documentElement.lang || "pt-BR";

  const base = destino.createElement("base");
  base.href = origem.baseURI;
  destino.head.appendChild(base);

  // O editor usa CSS próprio; os componentes MUI recebem um cache no novo documento.
  origem.querySelectorAll('link[rel="stylesheet"], style:not([data-emotion])').forEach((elemento) => {
    destino.head.appendChild(elemento.cloneNode(true));
  });

  const estilos = window.getComputedStyle(origem.documentElement);
  destino.documentElement.style.fontSize = estilos.fontSize;
  for (let indice = 0; indice < estilos.length; indice += 1) {
    const propriedade = estilos[indice];
    if (propriedade.startsWith("--sear-")) {
      destino.documentElement.style.setProperty(propriedade, estilos.getPropertyValue(propriedade));
    }
  }
  const estilosCorpo = window.getComputedStyle(origem.body);
  destino.body.style.margin = "0";
  destino.body.style.fontFamily = estilosCorpo.fontFamily;
  destino.body.style.color = estilosCorpo.color;
  destino.body.style.backgroundColor = estilosCorpo.backgroundColor;
  const container = destino.createElement("div");
  destino.body.appendChild(container);
  return container;
}

export function useJanelaAuxiliar({ aberto, titulo }) {
  const [destino, setDestino] = useState(null);

  const abrir = () => {
    if (destino && !destino.janela.closed) {
      destino.janela.focus();
      return true;
    }

    // Deve ocorrer diretamente no clique para respeitar as regras de pop-up.
    const janela = window.open("about:blank", "_blank", "popup=yes,width=800,height=740,resizable=yes,scrollbars=yes");
    if (!janela) return false;

    try {
      const container = prepararDocumento(janela, titulo);
      const cache = createCache({ key: "sear-janela", container: janela.document.head });
      setDestino({ janela, container, cache });
      janela.focus();
      return true;
    } catch {
      janela.close();
      return false;
    }
  };

  useEffect(() => {
    if (!aberto) setDestino(null);
  }, [aberto]);

  useEffect(() => {
    if (!destino) return undefined;
    const { janela, cache } = destino;
    const retornar = () => setDestino((atual) => atual === destino ? null : atual);
    const fecharComPagina = () => janela.close();
    janela.addEventListener("pagehide", retornar);
    window.addEventListener("beforeunload", fecharComPagina);

    return () => {
      janela.removeEventListener("pagehide", retornar);
      window.removeEventListener("beforeunload", fecharComPagina);
      cache.sheet.flush();
      if (!janela.closed) janela.close();
    };
  }, [destino]);

  return { destino, abrir, recolher: () => setDestino(null) };
}

export default function JanelaAuxiliar({ destino, children }) {
  if (!destino) return children;
  return createPortal(
    <CacheProvider value={destino.cache}>{children}</CacheProvider>,
    destino.container,
  );
}
